<template>
  <div class="opening">
    <nav class="opening__breadcrumb">
      <router-link class="opening__breadcrumb-link" to="/">{{ breadcrumb.home }}</router-link>
      <span class="opening__breadcrumb-separator">›</span>
      <span class="opening__breadcrumb-current">{{ breadcrumb.current }}</span>
    </nav>

    <div class="opening__card">
      <section
        v-for="section in sections"
        :key="section.key"
        class="opening__section"
      >
        <h2 class="opening__section-title">{{ section.title }}</h2>

        <div
          v-if="section.paragraphs.length"
          class="opening__rich-text"
        >
          <p
            v-for="(paragraph, index) in section.paragraphs"
            :key="`${section.key}-p-${index}`"
            v-html="paragraph"
          />
        </div>

        <ul
          v-if="section.items.length"
          class="opening__list"
        >
          <li
            v-for="(item, index) in section.items"
            :key="`${section.key}-li-${index}`"
            v-html="item"
          />
        </ul>

        <div
          v-if="section.subsections.length"
          class="opening__subsections"
        >
          <article
            v-for="(subsection, index) in section.subsections"
            :key="`${section.key}-sub-${index}`"
            class="opening__subsection"
          >
            <h3 class="opening__subsection-title">{{ subsection.title }}</h3>

            <div
              v-if="subsection.paragraphs.length"
              class="opening__rich-text opening__rich-text--sub"
            >
              <p
                v-for="(paragraph, subIndex) in subsection.paragraphs"
                :key="`${section.key}-sub-${index}-p-${subIndex}`"
                v-html="paragraph"
              />
            </div>

            <ul
              v-if="subsection.items.length"
              class="opening__list opening__list--sub"
            >
              <li
                v-for="(item, subIndex) in subsection.items"
                :key="`${section.key}-sub-${index}-li-${subIndex}`"
                v-html="item"
              />
            </ul>
          </article>
        </div>

        <ul
          v-if="section.links.length"
          class="opening__links"
        >
          <li
            v-for="(link, index) in section.links"
            :key="`${section.key}-link-${index}`"
          >
            <a
              :href="link.url"
              target="_blank"
              rel="noopener"
            >
              {{ link.label }}
            </a>
          </li>
        </ul>
      </section>
    </div>
  </div>
</template>

<script>
import { getLocaleBlock } from '@/utils/i18n';

export default {
  name: 'OpeningView',
  computed: {
    page() {
      return getLocaleBlock(this.$i18n, 'openingPage', {}) || {};
    },
    breadcrumb() {
      const crumb = this.page?.breadcrumb || {};
      return {
        home: crumb.home || 'Home',
        current: crumb.current || 'Opening',
      };
    },
    intro() {
      const intro = this.page?.intro || {};
      const paragraphs = Array.isArray(intro.paragraphs) ? [...intro.paragraphs] : [];
      const lead = intro.lead || (paragraphs.length ? paragraphs.shift() : '');

      return {
        title: intro.title || this.breadcrumb.current || 'Opening',
        lead,
        paragraphs,
      };
    },
    sections() {
      const list = Array.isArray(this.page?.sections) ? this.page.sections : [];

      return list
        .map((section, index) => {
          const paragraphs = Array.isArray(section?.paragraphs) ? section.paragraphs : [];
          const items = Array.isArray(section?.items) ? section.items : [];
          const links = Array.isArray(section?.links) ? section.links : [];
          const subsections = Array.isArray(section?.subsections)
            ? section.subsections.map((subsection, subIndex) => ({
                title: subsection?.title || '',
                paragraphs: Array.isArray(subsection?.paragraphs) ? subsection.paragraphs : [],
                items: Array.isArray(subsection?.items) ? subsection.items : [],
                key: `${section?.id || index}-sub-${subIndex}`,
              }))
            : [];

          return {
            key: section?.id || `section-${index}`,
            title: section?.title || '',
            paragraphs,
            items,
            links,
            subsections,
          };
        })
        .filter(
          (section) =>
            section.title ||
            section.paragraphs.length ||
            section.items.length ||
            section.links.length ||
            section.subsections.length,
        );
    },
  },
};
</script>

<style scoped>
.opening {
  max-width: 1040px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.8rem;
}

.opening__breadcrumb {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  color: #5b6783;
}

.opening__breadcrumb-link {
  color: inherit;
  text-decoration: none;
}

.opening__breadcrumb-link:hover {
  color: #1f4cff;
}

.opening__breadcrumb-separator {
  color: #a0abc5;
  font-size: 0.85rem;
}

.opening__breadcrumb-current {
  color: #1a2540;
  font-weight: 600;
}

.opening__card {
  background: #ffffff;
  border-radius: 28px;
  padding: 2.6rem 3.1rem 3rem;
  box-shadow: 0 22px 48px rgba(20, 37, 64, 0.12);
  border: 1px solid rgba(20, 37, 64, 0.08);
  display: flex;
  flex-direction: column;
  gap: 2.8rem;
}

.opening__intro {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.opening__title {
  margin: 0;
  font-size: 2rem;
  color: #12203a;
  letter-spacing: -0.01em;
}

.opening__lead {
  margin: 0;
  font-size: 1.08rem;
  line-height: 1.7;
  color: #2a3753;
}

.opening__paragraph {
  margin: 0;
  font-size: 1rem;
  line-height: 1.7;
  color: #36466b;
}

.opening__section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.opening__section-title {
  margin: 0;
  font-size: 1.65rem;
  color: #152542;
}

.opening__rich-text {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  font-size: 1rem;
  line-height: 1.75;
  color: #334468;
}

.opening__rich-text p {
  margin: 0;
}

.opening__list {
  margin: 0;
  padding-left: 1.2rem;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  color: #324366;
  line-height: 1.6;
}

.opening__list li {
  margin: 0;
}

.opening__subsections {
  display: flex;
  flex-direction: column;
  gap: 1.8rem;
  border-top: 1px solid rgba(26, 37, 64, 0.08);
  padding-top: 1.8rem;
}

.opening__subsection {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.opening__subsection-title {
  margin: 0;
  font-size: 1.25rem;
  color: #1e3357;
}

.opening__list--sub {
  padding-left: 1.1rem;
}

.opening__links {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.opening__links a {
  color: #c67c12;
  text-decoration: none;
  font-weight: 600;
}

.opening__links a:hover {
  color: #a86100;
  text-decoration: underline;
}

.opening__card a {
  color: #c67c12;
  font-weight: 600;
  text-decoration: none;
}

.opening__card a:hover {
  color: #a86100;
  text-decoration: underline;
}

@media (max-width: 900px) {
  .opening__card {
    padding: 2.2rem 2.2rem 2.6rem;
    border-radius: 24px;
  }

  .opening__title {
    font-size: 1.75rem;
  }

  .opening__section-title {
    font-size: 1.45rem;
  }
}

@media (max-width: 640px) {
  .opening {
    gap: 1.4rem;
  }

  .opening__card {
    padding: 2rem 1.6rem 2.4rem;
    border-radius: 20px;
    gap: 2rem;
  }

  .opening__title {
    font-size: 1.45rem;
  }

  .opening__section {
    gap: 0.8rem;
  }

  .opening__section-title {
    font-size: 1.3rem;
  }

  .opening__subsections {
    gap: 1.4rem;
    padding-top: 1.4rem;
  }
}
</style>
